export type ShopifyOptionalScopeErrorCode =
  | "app_bridge_unavailable"
  | "scope_declined";

export class ShopifyOptionalScopeError extends Error {
  readonly code: ShopifyOptionalScopeErrorCode;

  constructor(code: ShopifyOptionalScopeErrorCode) {
    super(code);
    this.name = "ShopifyOptionalScopeError";
    this.code = code;
  }
}

/**
 * Ask Shopify for one optional scope only when the merchant uses the feature.
 *
 * The App Bridge Scopes API is authoritative here. `Shop.scopes` in our local
 * database can lag a dynamic grant, while this API reports the permission that
 * the current app installation can use immediately.
 */
export async function ensureShopifyOptionalScope(scope: string): Promise<void> {
  const scopesApi =
    typeof window === "undefined" ? undefined : window.shopify?.scopes;
  if (!scopesApi) {
    throw new ShopifyOptionalScopeError("app_bridge_unavailable");
  }

  const current = await scopesApi.query();
  if (current.granted.includes(scope)) return;

  const requested = await scopesApi.request([scope]);
  if (!requested.detail.granted.includes(scope)) {
    throw new ShopifyOptionalScopeError("scope_declined");
  }
}
